import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, SimpleGrid, Stack } from "@mantine/core";
import { Alert, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconArrowRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { BreakdownRow, MonitoringDimension, TransactionItem } from "@models/adminMonitoring";
import { adminMonitoringApi } from "@services/adminMonitoringApi";
import { useApiResource } from "@hooks/monitoring/useApiResource";
import { useMonitoringFilters } from "@hooks/monitoring/useMonitoringFilters";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import BreakdownCard from "./BreakdownCard";
import KpiTiles from "./KpiTiles";
import SourceAlerts from "./SourceAlerts";
import TransactionsTable from "./TransactionsTable";
import UnpricedModels from "./UnpricedModels";
import ModelAlerts from "./ModelAlerts";
import UsageChart from "./UsageChart";
import WindowToolbar from "./WindowToolbar";

const SPLITS: MonitoringDimension[] = ["module", "service", "provider", "model", "capability", "user", "organisation"];

interface UsageOverviewProps {
  /** A string, or built from the organisation name the dashboard resolves. */
  title: string | ((organisationName: string | null | undefined) => string);
  description?: string;
  icon: React.ReactNode;
  /** Pin the view to one organisation (the organisation detail page); otherwise the URL decides. */
  organisationId?: string;
  /** Rendered between the header and the tiles (e.g. a back link). */
  before?: React.ReactNode;
}

/**
 * Usage at a glance: one `/admin/dashboard` call for the totals with the change
 * against the previous window, usage over time (split by any dimension), the
 * breakdowns and unpriced usage, and a separate call for the latest
 * transactions. The dashboard shows it for the caller's scope, the organisation
 * page for one organisation.
 */
const UsageOverview: React.FC<UsageOverviewProps> = ({ title, description, icon, organisationId, before }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canViewAllOrganisations } = useAdminPermissions();
  const monitoring = useMonitoringFilters();
  const { filters, preset, custom, values, setFilter, setPreset, refresh, granularity } = monitoring;
  const orgId = organisationId ?? values.org;
  const splitParam = monitoring.raw("split");
  const allOrgs = Boolean(canViewAllOrganisations && !orgId);
  const splits = SPLITS.filter((d) => d !== "organisation" || allOrgs);
  const split = (splits.includes(splitParam as MonitoringDimension) ? splitParam : "module") as MonitoringDimension;
  const scoped = { from: filters.from, to: filters.to, organisationId: orgId };

  // The latest transactions read the raw events of the window and wait for entity
  // enrichment, so they load on their own: tiles, chart and breakdowns render first.
  const dashboard = useApiResource(`dashboard:${JSON.stringify(scoped)}:${granularity}:${split}`, (signal) =>
    adminMonitoringApi().dashboard(scoped, { granularity, splitBy: split, recent: false }, signal));
  const recent = useApiResource(`recent:${JSON.stringify(scoped)}`, (signal) =>
    adminMonitoringApi().recentTransactions(scoped, 10, signal));
  const organisations = useApiResource(canViewAllOrganisations && !organisationId ? `orgs:${filters.from}:${filters.to}` : null,
    (signal) => adminMonitoringApi().facets({ from: filters.from, to: filters.to }, signal));
  const data = dashboard.data;

  const windowParams = () => {
    const params = new URLSearchParams({ range: preset });
    if (preset === "custom" && custom.from && custom.to) {
      params.set("from", custom.from);
      params.set("to", custom.to);
    }
    return params;
  };
  const toMonitoring = (extra: Record<string, string> = {}) => {
    const params = windowParams();
    if (orgId) params.set("org", orgId);
    Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    navigate(`/admin/monitoring?${params}`);
  };
  const onBreakdown = (dimension: MonitoringDimension) => {
    if (dimension === "module") return (row: BreakdownRow) => toMonitoring({ module: row.value });
    if (dimension === "organisation") {
      return (row: BreakdownRow) => navigate(`/admin/organisations/${encodeURIComponent(row.value)}?${windowParams()}`);
    }
    return undefined;
  };
  const open = (item: TransactionItem) =>
    navigate(`/admin/monitoring/transactions/${item.group_id}?${new URLSearchParams({ from: filters.from, to: filters.to })}`);

  // Extraction's health comes with the dashboard (its operations); the entity sources
  // with the latest transactions. Metering is in both: show each source once.
  const sources = [...(data?.sources ?? []), ...(recent.data?.sources ?? [])]
    .filter((source, index, all) => all.findIndex((s) => s.source === source.source) === index);

  const breakdowns: MonitoringDimension[] = ["module", "service", "provider", "model", allOrgs ? "organisation" : "user", "capability"];

  return (
    <Stack gap="md">
      <PageHeader
        title={typeof title === "function" ? title(data?.scope.organisation_name) : title}
        description={description}
        icon={icon}
        actions={
          <WindowToolbar
            preset={preset}
            custom={custom}
            onPresetChange={setPreset}
            onRefresh={() => { refresh(); dashboard.reload(); recent.reload(); }}
            loading={dashboard.loading}
            organisations={canViewAllOrganisations && !organisationId ? organisations.data?.organisations ?? [] : undefined}
            organisationId={values.org}
            onOrganisationChange={(value) => setFilter("org", value)}
          />
        }
      />
      {before}

      {dashboard.error && (
        <Alert color={dashboard.forbidden ? "gray" : "red"} title={t("admin.insights.loadFailed")}>{dashboard.error}</Alert>
      )}
      <SourceAlerts sources={sources} />

      <KpiTiles summary={data?.summary ?? null} previous={data?.previous_summary ?? null} operations={data?.operations}
                loading={dashboard.loading} />

      <UsageChart
        title={t("admin.insights.dashboard.usageOverTime")}
        data={data?.time_series ?? null}
        loading={dashboard.loading}
        splitOptions={splits}
        onSplitChange={(dimension) => monitoring.update({ split: dimension })}
      />

      <ModelAlerts alerts={data?.model_alerts} />
      <UnpricedModels byModel={data?.breakdowns.model} />

      <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
        {breakdowns.map((dimension) => (
          <BreakdownCard
            key={dimension}
            title={t(`admin.insights.dashboard.by.${dimension}`)}
            data={data?.breakdowns[dimension]}
            loading={dashboard.loading}
            onSelect={onBreakdown(dimension)}
          />
        ))}
      </SimpleGrid>

      <SectionCard
        title={t("admin.insights.dashboard.recent")}
        actions={
          <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />} onClick={() => toMonitoring()}>
            {t("admin.insights.dashboard.openMonitoring")}
          </Button>
        }
      >
        {recent.error && (
          <Alert color={recent.forbidden ? "gray" : "red"} title={t("admin.insights.loadFailed")}>{recent.error}</Alert>
        )}
        <TransactionsTable items={recent.data?.items ?? []} loading={recent.loading}
                           showOrganisation={allOrgs} onOpen={open} />
      </SectionCard>
    </Stack>
  );
};

export default UsageOverview;
